import SlideIcon from '@mui/icons-material/Flip';
import HighlightIcon from '@mui/icons-material/HighlightOutlined';
import OpactityIcon from '@mui/icons-material/Opacity';
import {
  Box,
  Button,
  Divider,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Tooltip,
  styled,
} from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {connect} from 'react-redux';

import CompareDiff from './compare-diff-slider.jsx';
import CompareFade from './compare-fade-slider.jsx';
import {setCompareMethod} from '@explorer/actions/compare';
import {setMode} from '@explorer/actions/ui';
import {trackEvent} from '@explorer/analytics';
import {COMPARE_METHODS, UI_MODES} from '@explorer/constants';

const StyledToolbar = styled(Toolbar)(({theme}) => ({
  minHeight: theme.spacing(6),
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0.5, 1),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(0.5, 1),
  },
}));

const StyledToggleButton = props => (
  <ToggleButton
    sx={{
      color: 'common.white',
      borderColor: 'grey.600',
      '&.Mui-selected': {
        backgroundColor: 'primary.main',
        '&:hover': {
          backgroundColor: 'primary.dark',
        },
      },
    }}
    {...props}
  />
);

const CompareTools = ({compareMethod, ...props}) => {
  const {t} = useTranslation();

  const onChangeMethod = (event, value) => {
    const compareMethod = value || COMPARE_METHODS.SLIDER;
    trackEvent('Changed compare method', {
      method: compareMethod,
    });
    props.setCompareMethod(compareMethod);
  };

  return (
    <Paper
      square
      elevation={5}
      sx={{
        boxShadow: 'none',
        zIndex: theme => theme.zIndex.appBar,
        minHeight: theme => theme.spacing(6),
        position: 'relative',
      }}
      className="pl-mode-dark"
    >
      <StyledToolbar variant="dense" data-qe="compare-tools">
        <Box
          sx={{
            display: 'flex',
          }}
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            value={compareMethod}
            onChange={onChangeMethod}
          >
            <StyledToggleButton
              value={COMPARE_METHODS.SLIDER}
              data-qe="compare-slider-button"
            >
              <Tooltip title={t('compareTools.slide', 'Slide to compare')}>
                <SlideIcon />
              </Tooltip>
            </StyledToggleButton>

            <StyledToggleButton
              value={COMPARE_METHODS.FADE}
              data-qe="compare-fade-button"
            >
              <Tooltip
                title={t('compareTools.adjustOpacity', 'Adjust layer opacity')}
              >
                <OpactityIcon />
              </Tooltip>
            </StyledToggleButton>

            <StyledToggleButton
              value={COMPARE_METHODS.DIFF}
              data-qe="compare-diff-button"
            >
              <Tooltip
                title={t(
                  'compareTools.relativeLuminance',
                  'Compare relative luminance'
                )}
              >
                <HighlightIcon />
              </Tooltip>
            </StyledToggleButton>
          </ToggleButtonGroup>

          <Divider
            orientation="vertical"
            sx={{margin: theme => theme.spacing(0, 1)}}
          />

          {compareMethod === COMPARE_METHODS.DIFF && (
            <CompareDiff
              onChange={props.onPixelDiffChange}
              range={props.pixelDiffRangeValue}
            />
          )}

          {compareMethod === COMPARE_METHODS.FADE && (
            <CompareFade
              value={props.fadeValue}
              onChange={props.onFadeChange}
            />
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Button
            variant="contained"
            color="primary"
            size="mini"
            onClick={() => props.setMode(UI_MODES.BROWSE)}
          >
            {t('compareTools.stopComparison')}
          </Button>
        </Box>
      </StyledToolbar>
    </Paper>
  );
};

CompareTools.propTypes = {
  compareMethod: PropTypes.string.isRequired,
  fadeValue: PropTypes.number,
  onFadeChange: PropTypes.func,
  onPixelDiffChange: PropTypes.func,
  pixelDiffRangeValue: PropTypes.array,
  setCompareMethod: PropTypes.func,
  setMode: PropTypes.func,
};

const mapStateToProps = state => ({
  compareMethod: state.compare.method,
});

const mapDispatchProps = {
  setCompareMethod,
  setMode,
};

export default connect(mapStateToProps, mapDispatchProps)(CompareTools);
